/**
 * Save / query / export / import helpers over the Dexie schema.
 *
 * `exportTournamentTxt` returns a single `.txt` file (PokerStars HH dialect)
 * for a completed or in-progress tournament. `importTournamentTxt` is its
 * inverse and idempotent on duplicate `hand.id` insertion — importing the
 * same file twice produces one set of rows, not duplicates.
 */
import type { Action } from '@/engine/types';
import { getDb, type HandResultRow, type HandRow, type PokerDb, type TournamentRow } from './db';
import { type HandRecord, parseFile, serializeHand } from './pokerstars';

export function handId(tournamentId: string, handNumber: number): string {
  return `${tournamentId}:${handNumber}`;
}

function resolveDb(db?: PokerDb): PokerDb {
  return db ?? getDb();
}

export async function saveTournament(row: TournamentRow, db?: PokerDb): Promise<void> {
  await resolveDb(db).tournaments.put(row);
}

export async function updateTournament(
  id: string,
  patch: Partial<TournamentRow>,
  db?: PokerDb,
): Promise<void> {
  await resolveDb(db).tournaments.update(id, patch);
}

export async function saveHand(row: HandRow, db?: PokerDb): Promise<void> {
  await resolveDb(db).hands.put(row);
}

export async function listTournaments(db?: PokerDb): Promise<TournamentRow[]> {
  return resolveDb(db).tournaments.orderBy('startedAt').toArray();
}

export async function getTournament(id: string, db?: PokerDb): Promise<TournamentRow | undefined> {
  return resolveDb(db).tournaments.get(id);
}

export async function listHands(tournamentId: string, db?: PokerDb): Promise<HandRow[]> {
  return resolveDb(db)
    .hands.where('[tournamentId+handNumber]')
    .between([tournamentId, 0], [tournamentId, Number.MAX_SAFE_INTEGER], true, true)
    .toArray();
}

export async function getHand(id: string, db?: PokerDb): Promise<HandRow | undefined> {
  return resolveDb(db).hands.get(id);
}

/**
 * Convert a HandRecord (canonical in-memory shape) into the persisted HandRow.
 * Caller supplies the serialized HH text via `pokerstarsHH` so the writer does
 * not have to know about Dexie.
 */
export function rowFromRecord(record: HandRecord, pokerstarsHH: string): HandRow {
  const result: HandResultRow = {
    payouts: record.payouts,
    boardRevealedTo: record.board.length === 5 ? 'river' : 'showdown',
  };
  return {
    id: handId(record.tournamentId, record.handNumber),
    tournamentId: record.tournamentId,
    handNumber: record.handNumber,
    seed: record.seed,
    level: record.level.level,
    pokerstarsHH,
    actions: record.actions,
    result,
    playedAt: record.playedAt,
  };
}

/**
 * Concatenate every hand of a tournament (ordered) into a single HH text.
 * Hands are separated by a blank line so `parseFile` can split them back
 * on the `# meta:` sentinel line.
 */
export async function exportTournamentTxt(tournamentId: string, db?: PokerDb): Promise<string> {
  const rows = await listHands(tournamentId, db);
  if (rows.length === 0) {
    throw new Error(`exportTournamentTxt: no hands for tournament ${tournamentId}`);
  }
  return `${rows.map((r) => r.pokerstarsHH.replace(/\n$/, '')).join('\n\n')}\n`;
}

/**
 * Import a tournament from HH text. Creates a `tournaments` row if absent
 * (derived meta: first/last playedAt, chips-in-winner as prizePoints) and
 * inserts all hands. Idempotent: Dexie `put` replaces on duplicate PK so
 * re-importing the same file is a no-op beyond the row timestamps.
 */
export async function importTournamentTxt(
  text: string,
  db?: PokerDb,
): Promise<{ tournamentId: string; handIds: string[] }> {
  const records = parseFile(text);
  if (records.length === 0) {
    throw new Error('importTournamentTxt: no hands parsed from input text');
  }
  const first = records[0] as HandRecord;
  const tournamentId = first.tournamentId;
  // Sanity: every hand must belong to the same tournament.
  for (const r of records) {
    if (r.tournamentId !== tournamentId) {
      throw new Error(
        `importTournamentTxt: mixed tournamentIds ${tournamentId} vs ${r.tournamentId}`,
      );
    }
  }
  const d = resolveDb(db);
  const startedAt = first.playedAt;
  const last = records[records.length - 1] as HandRecord;
  const existing = await d.tournaments.get(tournamentId);
  const tournamentRow: TournamentRow = existing ?? {
    id: tournamentId,
    startedAt,
    endedAt: last.playedAt,
    resultRank: null,
    prizePoints: 0,
    meta: { playerIds: first.seats.map((s) => s.playerId) },
  };
  const handRows: HandRow[] = [];
  for (const rec of records) {
    const pokerstarsHH = serializeHand(rec);
    handRows.push(rowFromRecord(rec, pokerstarsHH));
  }
  await d.transaction('rw', d.tournaments, d.hands, async () => {
    await d.tournaments.put(tournamentRow);
    await d.hands.bulkPut(handRows);
  });
  return { tournamentId, handIds: handRows.map((r) => r.id) };
}

/** Deserialize back to engine actions — pass-through for callers outside storage. */
export function actionsFromHand(row: HandRow): ReadonlyArray<Action> {
  return row.actions;
}
